import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardTitle,
    IonCol,
    IonContent,
    IonPage,
    IonRow,
} from "@ionic/react";
import React, { useContext, useState, useEffect } from "react";
import PageList from "../../../../components/Lists/PageList";
import Header from "../../../../components/Headers/Header";
import PageTitle from "../../../../components/PageTitle/PageTitle";
import Spinner from "../../../../components/Spinners/Spinner";
import { PersonContext } from "../../../../context/PersonContext";
import { Team } from "../../../../models";
import BasicTeamInfo from "../../../../components/Team/BasicTeamInfo";
import { teamListOptions } from "../../../../interfaces/Page";
import ErrorAlert from "../../../../components/Errors/ErrorAlert";
import SelectTeam from "../../../../components/Team/SelectTeam";
import { getTeamsForCurrentUser } from "../../../../utilities/team/TeamUtils";

const TeamPage: React.FC = () => {
    const user = useContext(PersonContext);

    const [currentTeam, setCurrentTeam] = useState<Team | null | undefined>();
    const [allTeams, setAllTeams] = useState<Team[] | null | undefined>();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const getTeams = async () => {
            setIsLoading(true);
            const allTeamsForUser = await getTeamsForCurrentUser(user, false);
            setAllTeams(allTeamsForUser || null)
            setCurrentTeam(allTeamsForUser ? allTeamsForUser[0] : null);
            setIsLoading(false);
        };
        if (user) getTeams();
        else setError("No user info found. Please reload the page. If you need customer support, contact hello@ringsidepro.com.");
    }, [user.id]);

    const handleSelectedTeam = (selectedTeam: Team) => {
        setCurrentTeam(selectedTeam);
    }

    return (
        <IonPage className="bg-light">
            <Header />
            <IonContent>
                <PageTitle title="Team" />
                {error && <ErrorAlert error={error} />}
                {isLoading ?
                    <Spinner />
                    :
                    <>
                        {/* If there is more than one team, show a dropdown select of all teams associated with this user */}
                        {(!!allTeams && (allTeams.length > 1)) &&
                            <IonRow>
                                <IonCol sizeXs="10" sizeMd="8" sizeLg="6">
                                    <SelectTeam hideLabel selectedValue={currentTeam?.id || null} teamsToChooseFrom={allTeams} onSelect={handleSelectedTeam} />
                                </IonCol>
                            </IonRow>
                        }

                        {/* If there is at least one team, show the info for the selected team */}
                        {(!!allTeams && (allTeams.length > 0)) ? 
                            <>
                                {currentTeam ?
                                    <>
                                        {/* Show the info for the selected team */}
                                        <IonRow className="ion-text-center ion-justify-content-center">
                                            <IonCol sizeSm="12" sizeMd="8">
                                                <BasicTeamInfo team={currentTeam} />
                                            </IonCol>
                                        </IonRow>
                                        <IonRow className="ion-text-center ion-justify-content-center">
                                            <IonCol sizeSm="12" sizeMd="8">
                                                <PageList id={currentTeam.id} includeId={true} listOptions={teamListOptions} />
                                            </IonCol>
                                        </IonRow>
                                    </>
                                    :
                                    // If an error occurs and there is no current team, show some info about how to get to a team
                                    <IonRow className="ion-justify-content-center">
                                        <IonCol sizeSm="12" sizeMd="12">
                                            <IonCard mode="md" className="ion-padding bg-white">
                                                <IonCardTitle>Select a Team</IonCardTitle>
                                                <IonCardContent className="ion-text-center">
                                                    <p className="ion-text-center pb-4">
                                                        Use the dropdown above to select a team.
                                                    </p>
                                                    <p className="ion-text-center pb-4">
                                                        Or join a new team by completing an application.
                                                    </p>
                                                    <a href="/index/ael/home">
                                                        <IonButton>
                                                            Go to Dashboard
                                                        </IonButton>
                                                    </a>
                                                </IonCardContent>
                                            </IonCard>
                                        </IonCol>
                                    </IonRow>
                                }
                            </>                            
                        :
                        // If no teams, then redirect to Dashboard to join a team
                        <IonRow className="ion-justify-content-center">
                            <IonCol sizeSm="12" sizeMd="12">
                                <IonCard mode="md" className="ion-padding bg-white">
                                    <IonCardTitle>Join a Team</IonCardTitle>
                                    <IonCardContent className="ion-text-center">
                                        <p className="ion-text-center pb-4">
                                            You are not currently a member of a team. 
                                            Go to the Dashboard page and complete an application in order to join a team.
                                        </p>
                                        <a href="/index/ael/home">
                                            <IonButton>
                                                Go to Dashboard
                                            </IonButton>
                                        </a>
                                    </IonCardContent>
                                </IonCard>
                            </IonCol>
                        </IonRow>
                        }
                    </>
                }
            </IonContent>
        </IonPage>
    );
};

export default TeamPage;